import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  TextField,
  Stack,
  Grid,
} from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import "./ServicesGasFlowOptimizer.scss";
import { useTranslation } from "react-i18next";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import {
  ManagementRequestInfo,
  PostCallCenterRequestBody,
  RequestStateDescriptionData,
} from "../../core/models/managements/managements.model";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import ServicesHeader from "../ServicesUpdate/ServicesUpdateHeader/ServicesUpdateHeader";
import ConsumptionsManagementsDeliveryAddressComponent from "../ConsumptionsManagementsDeliveryAddressComponent/ConsumptionsManagementsDeliveryAddressComponent";
import { initialOwnerDataCup } from "../../utils/ownerData/ownerData.util";
import ManagementsRequestState from "../Managements/ManagementsRequestState/ManagementsRequestState";
import {
  getManagementsRequestTypeData,
  initialManagementRequestInfo,
} from "../../utils/components/managements/managements.util";
import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
  SERVICES_SUBTYPE_OPTIMIZE_GAS,
} from "../../core/constants/constants";
import {
  postCallCenter as postCallCenterFetch,
  postImportDocument,
} from "../../core/services/managements/managements.service";
import { formatDate } from "../../utils/dateUtils/formatDate.util";
import ManagementUploadFile from "../Managements/ManagementsUploadFile/ManagementsUploadFile";
import ManagementsCancelProcess from "../Managements/ManagementsCancelProcess/ManagementsCancelProcess";
import theme from "../../muiTheme";
import {
  getRecipientId,
  getServiceSubTypeId,
  getServiceTypeId,
} from "../../utils/components/services/services.util";

const ServicesGasFlowOptimizer = () => {
  const { t } = useTranslation();
  const { ownerData, userData } = useContext(TotalEnergiesContext);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] =
    useState<OwnerDataCups>(initialOwnerDataCup);
  const [managementRequestInfo, setManagementRequestInfo] =
    useState<ManagementRequestInfo>(initialManagementRequestInfo);
  const [step, setStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<string>("");
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>({
      title: "",
      description: "",
      descriptionExtra: "",
    });
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const checkButtonDisabled = useCallback(() => {
    if (selectedDeliveryAddress.idCups) {
      setIsButtonDisabled(false);
      return;
    }
    setIsButtonDisabled(true);
  }, [selectedDeliveryAddress]);

  useEffect(() => {
    checkButtonDisabled();
  }, [checkButtonDisabled]);

  const handleSendRequest = () => {
    if (ownerData && userData) {
      postCallCenter(message, selectedDeliveryAddress);
    }
  };

  const postCallCenter = (
    incidenceText: string,
    ownerDataCupSelected: OwnerDataCups
  ): void => {
    const { contractCode, envirorment, idContract } = ownerDataCupSelected;
    const managementMessage = `El usuario ha realizado una petición de optimización de caudal.${
      incidenceText ? ` Detalle de la petición: ${incidenceText}.` : ""
    }${file ? ` Archivo adjunto: ${file.name}.` : ""}`;

    if (userData) {
      const idRegisterType = getServiceTypeId(envirorment);
      const IdSubTipoReclamacion = getServiceSubTypeId(
        idRegisterType,
        SERVICES_SUBTYPE_OPTIMIZE_GAS
      );
   
      const requestBody: PostCallCenterRequestBody = {
        TextoIncidencia: managementMessage,
        CodigosContratos: [+contractCode],
        IdTipoRegistro: idRegisterType,
        IdsContratos: [+idContract],
        IdSubTipoReclamacion,
        idUsuarioDestino: getRecipientId(idRegisterType, IdSubTipoReclamacion),
      };
      setIsLoading(true);
      setStep(1);

      postCallCenterFetch(requestBody, userData.token)
        .then(([{ IdCallCenter, IdCarpetaIncidencias }]) => ({
          IdCallCenter,
          IdCarpetaIncidencias,
        }))
        .then(async ({ IdCallCenter, IdCarpetaIncidencias }) => {
          file &&
            (await postImportDocument(
              IdCallCenter,
              IdCarpetaIncidencias,
              file,
              userData.token
            ));
          setRequestState(MANAGEMENTS_STATE_PENDING);
          setRequestStateData(
            getManagementsRequestTypeData(
              SERVICES_SUBTYPE_OPTIMIZE_GAS,
              MANAGEMENTS_STATE_PENDING
            )
          );
          setManagementRequestInfo({
            registrarionDate: formatDate(new Date()),
            refferenceNumber: IdCallCenter.toString(),
          });
          setIsLoading(false);
        })
        .catch((error) => {
          setRequestState(MANAGEMENTS_STATE_ERROR);
          setRequestStateData(
            getManagementsRequestTypeData(
              SERVICES_SUBTYPE_OPTIMIZE_GAS,
              MANAGEMENTS_STATE_ERROR
            )
          );
          setIsLoading(false);
        });
    }
  };
  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <Box
      component="section"
      className="last-managements"
      paddingBottom={{ md: "5rem" }}
    >
      <ServicesHeader
        title={"Optimizador de caudal"}
        step={step}
        showStepper={true}
      />

      {step === 0 && (
        <Grid container justifyContent="center">
          <Grid item md={8} lg={7} flex={1}>
            <Stack
              bgcolor={{ md: "#fff" }}
              borderRadius="12px"
              boxShadow={{ md: "0px 3px 5px 0px rgba(55, 70, 73, 0.20)" }}
              padding="3rem"
              marginY="2.5rem"
              gap={{ xs: "2rem", md: "2.5rem" }}
            >
              <Stack gap="1rem">
                <Typography component="h3" variant="h3">
                  {t("Servicios.Seleccione el CUPS")}
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  color={theme.palette.grey[700]}
                >
                  {t(
                    "Servicios.Nos basaremos en su consumo de los 12 meses anteriores para hacerle llegar sugerencias para optimizar su caudal."
                  )}
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  color={theme.palette.grey[700]}
                >
                  {t(
                    "Servicios.Seleccione el suministro sobre el cual desea recibir sugerencias de optimización de caudal."
                  )}
                </Typography>

                <Stack width={{ xs: "100%", md: "50%" }}>
                  <ConsumptionsManagementsDeliveryAddressComponent
                    ownerDataCups={ownerData?.cups || []}
                    ownerDataCupSelected={selectedDeliveryAddress}
                    excludeElectricity={true}
                    setOwnerDataCupSelected={(
                      ownerDataCupSelected: OwnerDataCups
                    ) => setSelectedDeliveryAddress(ownerDataCupSelected)}
                  />
                </Stack>
              </Stack>
              <Stack gap="1rem">
                <Typography component="h3" variant="h3">
                  {t("Servicios.Información adicional")}
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  color={theme.palette.grey[700]}
                >
                  {t(
                    "Servicios.Opcionalmente puede dejarnos un mensaje que sea necesario para completar el servicio."
                  )}
                </Typography>

                <FormControl
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    paddingTop: "0.5rem",
                  }}
                >
                  <FormLabel htmlFor="message">
                    <Typography fontWeight={600}>
                      {t("Gestiones.Mensaje")}
                      <> ({t("Opcional")})</>
                    </Typography>
                  </FormLabel>
                  <TextField
                    multiline
                    rows={7}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  ></TextField>
                </FormControl>
              </Stack>
              <Stack gap="1rem">
                <Typography component="h3" variant="h3">
                  {t("Gestiones.Anexo")}
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  color={theme.palette.grey[700]}
                >
                  {t(
                    "Servicios.Opcionalmente puede adjuntarnos datos de su consumo."
                  )}
                </Typography>

                <ManagementUploadFile
                  title={t("Adjuntos")}
                  multipleFiles={false}
                  onFileUpload={(id, file) => setFile(file)}
                  onFileRemove={(id) => {
                    setFile(null);
                  }}
                />
              </Stack>
              <FormControl
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  gap: "1rem",
                  marginY: "1.5rem",
                }}
              >
                <ButtonElement
                  onClick={() => handleSendRequest()}
                  disabled={isButtonDisabled}
                >
                  {t("Servicios.Solicitar optimización")}
                </ButtonElement>
                <ButtonElement type="button" secondary onClick={openModal}>
                  {t("Cancelar")}
                </ButtonElement>
              </FormControl>
            </Stack>

            <ManagementsCancelProcess
              open={isModalOpen}
              handleClose={closeModal}
            />
          </Grid>
        </Grid>
      )}
      {[1, 2].includes(step) && (
        <ManagementsRequestState
          state={requestState}
          isLoading={isLoading}
          managementRequestInfo={managementRequestInfo}
          showManagementRequestInfo
          title={requestStateData.title}
          description={requestStateData.description}
          descriptionExtra={requestStateData.descriptionExtra}
        />
      )}
    </Box>
  );
};

export default ServicesGasFlowOptimizer;
